import { FormEvent, useState } from 'react';
import { defineMessages } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import Link from '@alltrails/shared/denali/components/Link';
import OutlinedTextField from '@alltrails/shared/components/OutlinedTextField';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import CurrencyCode from 'types/CurrencyCode';
import * as styles from './styles/styles.module.scss';
import { serverAjaxPromise } from '../../../utils/requests/request_helpers';

const messages = defineMessages({
  ERR_TRY_AGAIN: { defaultMessage: 'Error, please try again' },
  MOBILE_GIFT: {
    defaultMessage: 'Your account has a current mobile subscription. Please read our <link>FAQ</link> to learn how to redeem your gift.'
  },
  ONCE_YOU_REDEEM: {
    defaultMessage:
      'Once you redeem the gift subscription, your account will be updated and you can start enjoying the benefits of AllTrails+. Your subscription begins on the redemption date and will be active for the length of the plan that was purchased.'
  },
  REDEEM: { defaultMessage: 'Redeem' },
  REDEMPTION_CODE: { defaultMessage: 'Redemption code' }
});

type RedemptionFormProps = {
  couponCode?: string;
  currencyCode?: CurrencyCode;
};

const RedemptionForm = ({ couponCode = null, currencyCode = 'USD' }: RedemptionFormProps) => {
  const {
    formatMessage,
    formattedDefaultMessages: { ERR_TRY_AGAIN, ONCE_YOU_REDEEM, REDEEM, REDEMPTION_CODE }
  } = useFormatMessage(messages);
  const [code, setCode] = useState(couponCode || '');
  const [loading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [giftMobileError, setGiftMobileError] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (hasError) {
      setError(false);
    }

    if (giftMobileError) {
      setGiftMobileError(false);
    }

    const { value } = e.target;
    setCode(value);
  };

  const redeemGiftCode = () =>
    serverAjaxPromise({
      type: 'POST',
      contentType: 'application/json',
      url: `/api/alltrails/gift/${code}/redeem`,
      data: JSON.stringify({
        gift_id: code,
        currency: currencyCode
      })
    });

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      await redeemGiftCode();
      window.location.href = '/plus/welcome';
    } catch (err) {
      if (err.responseJSON && err.responseJSON.errors) {
        const giftMobileSubActive = err.responseJSON.errors.find((error: any) => error.code === 'gift_mobile_sub_active');

        if (giftMobileSubActive) {
          setGiftMobileError(true);
        }
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formattedMobileGiftStr = formatMessage('MOBILE_GIFT', {
    // eslint-disable-next-line react/no-unstable-nested-components
    link: (str: string) => (
      <Link
        href="https://support.alltrails.com/hc/${languageCode}/articles/360052407531-I-already-have-a-Pro-subscription-through-iTunes-or-Google-Play-how-can-I-redeem-the-gift-I-received-"
        target="_blank"
        rel="noreferrer"
        size="md"
        testId="mobile-gift-faq"
      >
        {str}
      </Link>
    )
  });

  return (
    <section>
      <form id="redemption-form" onSubmit={onSubmit} className={styles.formContainer}>
        <OutlinedTextField type="text" changeHandler={onChange} value={code} label={REDEMPTION_CODE} errorMessage={hasError && ERR_TRY_AGAIN} />
        {giftMobileError && <div className={styles.giftMobileErrorStyles}>{formattedMobileGiftStr}</div>}
        <div className={styles.formButtonContainer}>
          <Button disabled={hasError} loading={loading} testId="redemption-form-redeem" text={REDEEM} type="submit" variant="primary" width="full" />
        </div>
      </form>
      <div className={styles.formSubText}>{ONCE_YOU_REDEEM}</div>
    </section>
  );
};

export default RedemptionForm;
